import { cachedRequest, invalidateCachedRequests, trackRequest } from '../utilities/request-cache.js';
import { notifySessionExpired } from '../utilities/session.js';

function assertValidAccountEndpoint(endPoint) {
    const normalizedEndpoint = endPoint.startsWith('/') ? endPoint : '/' + endPoint;
    // Accountgegevens horen uitsluitend bij een bestaand, ingelogd account.
    // Blokkeer dit centraal zodat geen enkele communicator per ongeluk een
    // URL met null, undefined, lege waarde of 0 kan versturen.
    if (/^\/v2\/accounts\/(?:0|null|undefined)(?:\/|$)/i.test(normalizedEndpoint)
        || /^\/v2\/accounts\/\/(?:\/|$)/.test(normalizedEndpoint)) {
        throw new Error('A valid logged-in account id is required');
    }
}

async function sbFetchRaw(endPoint, method, data) {
    assertValidAccountEndpoint(endPoint);
    const urlAPI = (process.env.REACT_APP_URL_API || '').replace(/\/+$/, '');
    const normalizedEndpoint = endPoint.startsWith('/') ? endPoint : '/' + endPoint;
    const urlEndPoint = urlAPI + normalizedEndpoint;
    let params;

    const controller = new window.AbortController();
    const signal = controller.signal;

    if (method === 'GET' || method === 'DELETE') {
        params = {
            method: method,
            headers: new Headers({
            }),
            signal : signal
        }
    } 

    if (method === 'POST') {
        params = {
            method: method,
            body: JSON.stringify(data),
            headers: new Headers({
            }),
            signal : signal
        }
    } 

    if (method === 'PUT') {
        params = {
            method: method,
            body: JSON.stringify(data),
            headers: new Headers({
            }),
            signal : signal
        }
    } 

    try {      
        let response = await fetch(urlEndPoint, params);
        let responseJson = await response.json();
        if(responseJson !== null) {
            let arrData = [];
            arrData = responseJson;
            return arrData;
        }
    } catch (error) {
        console.log('SbC fetch error: ', urlEndPoint, error.message);
        if (error.name == 'AbortError') {
            console.log('SbC abort error')
        } else {
            throw error;
        }
    }
}

// GET-reads worden kort gedeeld; reads via POST alleen als de communicator een
// ttl opgeeft. Elke andere POST/PUT/DELETE is een schrijfactie en wist alle
// gecachte en lopende reads, zodat je na een eigen wijziging nooit oude data
// ziet. Uitzondering: statische data en order-statusvlaggen, die door eigen
// writes niet veranderen (dat wissen liet de cart ze tientallen keren ophalen).
// POSTs naar catalogus, auteurs, producten, zoeken, content en tools zijn reads
// en wissen niets; monitoring schrijft alleen logregels.
const DEFAULT_GET_TTL = 10000;
const READ_ONLY_POST = /^\/?(?:catalog|search|content|tools|settings|translation|calculations|products|monitoring|v2\/catalog|v2\/search|v2\/calculations|v2\/authors|v2\/products)\/(?!.*(?:upload|copy|save|add))/i;
const SURVIVES_WRITES = /getSettings|getFeatures|getLanguages|\/tools\/countries|getBookDetailsBySku|checkShopForSku|getBookDownloads|\/v2\/authors\/|getShippingCosts|getPaymentStatus|getOrderStatus|Overruled|checkShippingCostsCustom/;
const clearAfterWrite = () => invalidateCachedRequests((key) => !SURVIVES_WRITES.test(key));

function withRequestCache(rawFetch, endPoint, method, data, options, identity) {
    const ttl = options && options.ttl !== undefined
        ? options.ttl
        : (method === 'GET' ? DEFAULT_GET_TTL : undefined);

    if (ttl === undefined) {
        if (READ_ONLY_POST.test(endPoint)) return trackRequest(rawFetch(endPoint, method, data));
        clearAfterWrite();
        const result = trackRequest(rawFetch(endPoint, method, data));
        result.then(clearAfterWrite, clearAfterWrite);
        return result;
    }

    const key = [method, endPoint, data === undefined ? '' : JSON.stringify(data), identity || ''].join(' ');
    return cachedRequest(key, () => rawFetch(endPoint, method, data), ttl);
}

export function sbFetch(endPoint, method, data, options) {
    return withRequestCache(sbFetchRaw, endPoint, method, data, options);
}

export function authFetch(endPoint, method, data, options) {
    // Token in de key: na in- of uitloggen nooit andermans resultaat delen.
    return withRequestCache(authFetchRaw, endPoint, method, data, options, getAuthToken() || '');
}

function getAuthToken(requireUserToken = false) {
    try {
        const stored = localStorage.getItem('APP_USER');
        if (stored) {
            const userData = JSON.parse(stored);
            const userToken = userData.user_token || userData.token || userData.guid;
            if (userToken) return userToken;
        }
    } catch {}

    // A shared frontend API key is not an account identity and must never be
    // used to call an account-specific endpoint.
    return requireUserToken ? null : process.env.REACT_APP_API_KEY;
}

async function authFetchRaw(endPoint, method, data) {
    assertValidAccountEndpoint(endPoint);
    const urlAPI = (process.env.REACT_APP_URL_API || '').replace(/\/+$/, '');
    const normalizedEndpoint = endPoint.startsWith('/') ? endPoint : '/' + endPoint;
    const urlEndPoint = urlAPI + normalizedEndpoint;
    let params;

    const controller = new window.AbortController();
    const signal = controller.signal;

    const accountEndpoint = /^\/v2\/accounts\/\d+(?:\/|$)/i.test(normalizedEndpoint);
    const token = getAuthToken(accountEndpoint);
    const authHeaders = new Headers();
    if (token) authHeaders.set('Authorization', 'Bearer ' + token);

    if (method === 'DELETE') {
        params = {
            method: method,
            headers: authHeaders
        }
    }

    if (method === 'GET') {
        params = {
            method: method,
            headers: authHeaders,
            signal: signal
        }
    }

    if (method === 'POST') {
        params = {
            method: method,
            body: JSON.stringify(data),
            headers: authHeaders,
            signal: signal
        }
    }

    if (method === 'PUT') {
        params = {
            method: method,
            body: JSON.stringify(data),
            headers: authHeaders,
            signal: signal
        }
    } 

    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
        const response = await fetch(urlEndPoint, { ...params, signal });
        if (!response.ok) {
            const error = new Error('Het verzoek kon niet worden verwerkt.');
            error.status = response.status;
            // 401: token onbekend; 403: token hoort bij een ander account. Bij een
            // eigen account-endpoint is de sessie in de browser dan niet meer geldig.
            if (accountEndpoint && (response.status === 401 || response.status === 403)) {
                error.sessionExpired = true;
                notifySessionExpired();
            }
            throw error;
        }
        const result = await response.json();
        if (!result || result.error || result.msg === 'error' || result.msg?.error) {
            throw new Error('De server kon het verzoek niet verwerken.');
        }
        return result;
    } finally {
        clearTimeout(timeout);
    }
}

export function abortFetch(endPoint, method, data, controller, signal) {
    const urlAPI = (process.env.REACT_APP_URL_API || '').replace(/\/+$/, '');
    const normalizedEndpoint = endPoint.startsWith('/') ? endPoint : '/' + endPoint;
    const urlEndPoint = urlAPI + normalizedEndpoint;
    
    const promise = new Promise(async (resolve) => {
        try {
            const response = await fetch(urlEndPoint, {
                method: method,
                body: JSON.stringify(data),
                signal
            });
            const data2 = await response.json();
            //assertIsCharacter(data);
            resolve(data2);
        } catch (error) {
            console.log('SbC fetch abort error')
        }
    });

    promise.cancel = () => controller.abort();

    return promise;
}
